import React, { useState, useEffect } from 'react';
import { useNavigation } from '../navigation';

// Utility function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper to clear auth-related data
const clearAuthSession = () => {
  try {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('userRole');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('username');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userId');
  } catch (e) {
    console.error('Error clearing auth session:', e);
  }
};

const RecentPurchasesBlock: React.FC = () => {
  const { navigateTo } = useNavigation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [recentPurchases, setRecentPurchases] = useState<any[]>([]);
  const [isLoadingPurchases, setIsLoadingPurchases] = useState(false);
  const [purchasesError, setPurchasesError] = useState<string | null>(null);
  const [receivingPurchaseId, setReceivingPurchaseId] = useState<number | null>(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Central handler for 401/unauthorized responses
  const handleUnauthorized = () => {
    clearAuthSession();
    navigateTo('landing');
  };

  // Fetch recent purchases
  const fetchRecentPurchases = async () => {
    try {
      setIsLoadingPurchases(true);
      setPurchasesError(null);
      
      const authToken = getAuthToken();
      if (!authToken) {
        handleUnauthorized();
        throw new Error('Authentication token not found');
      }

      const response = await fetch('https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized();
        }
        throw new Error(`Failed to fetch purchases: ${response.status}`);
      }

      const data = await response.json();
      
      // Handle different response formats
      const purchases = data.purchases || data.data || data || [];
      setRecentPurchases(Array.isArray(purchases) ? purchases : []);
      
    } catch (error: any) {
      console.error('Error fetching recent purchases:', error);
      setPurchasesError(error.message);
    } finally {
      setIsLoadingPurchases(false);
    }
  };

  // Handle receive purchase
  const handleReceivePurchase = async (purchaseId: number) => {
    try {
      setReceivingPurchaseId(purchaseId);
      
      const authToken = getAuthToken();
      if (!authToken) {
        handleUnauthorized();
        throw new Error('Authentication token not found');
      }

      console.log('Attempting to receive purchase:', purchaseId);
      console.log('Auth token:', authToken ? 'Present' : 'Missing');

      const response = await fetch(`https://pharma-amd-0-1-42.onrender.com/pharma/api/purchases/${purchaseId}/receive`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true'
        },
        body: JSON.stringify({
          purchase_id: purchaseId
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        if (response.status === 401) {
          handleUnauthorized();
        }
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to receive purchase: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log('Purchase received successfully:', result);
      
      // Refresh the purchases list
      await fetchRecentPurchases();
      
      // Show success notification
      setShowSuccessNotification(true);
      
      // Hide notification after 3 seconds
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 3000);
      
    } catch (error: any) {
      console.error('Error receiving purchase:', error);
      // Enhanced CORS error handling
      let errorMessage = error.message || 'Unknown error';
      if (error.message && (error.message.includes('CORS') || error.message.includes('Failed to fetch') || 
          error.message.includes('Access-Control-Allow-Origin') || error.message.includes('network') ||
          error.message.includes('ERR_FAILED'))) {
        errorMessage = 'CORS Error: Unable to connect to the server. Please check your connection and ensure the server allows requests from this origin.';
      }
      alert(`Failed to receive purchase: ${errorMessage}`);
    } finally {
      setReceivingPurchaseId(null);
    }
  };

  // Load recent purchases on component mount
  useEffect(() => {
    fetchRecentPurchases();
  }, []);

  return (
    <>
      {/* Success Notification */}
      {showSuccessNotification && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10000,
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: '1rem 2rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.75rem',
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
          animation: 'slideDownFromTop 0.5s ease-out',
          fontSize: '1rem',
          fontWeight: '600'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 12l2 2 4-4"/>
            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
          </svg>
          <span>Successfully Received!</span>
          <button
            onClick={() => setShowSuccessNotification(false)}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: 'white',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* Purchases Content */}
      {isLoadingPurchases ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '3rem 1.5rem' : '4rem 2rem',
          textAlign: 'center'
        }}>
          <div style={{
            width: isMobile ? '48px' : '56px',
            height: isMobile ? '48px' : '56px',
            border: '4px solid rgba(16, 185, 129, 0.1)',
            borderTop: '4px solid #10b981',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1.5rem'
          }}></div>
          <div style={{
            fontSize: isMobile ? '1rem' : '1.1rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '0.5rem'
          }}>
            Loading Purchases...
          </div>
          <div style={{
            fontSize: '0.85rem',
            color: '#6b7280'
          }}>
            Please wait while we fetch your recent purchases
          </div>
        </div>
      ) : purchasesError ? (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 251, 235, 0.95) 0%, rgba(254, 243, 199, 0.95) 100%)',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          borderRadius: '12px',
          padding: isMobile ? '1.25rem 1rem' : '1.5rem 1.5rem',
          textAlign: 'center',
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <div style={{
              width: isMobile ? '48px' : '56px',
              height: isMobile ? '48px' : '56px',
              background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)'
            }}>
              <svg width={isMobile ? "24" : "28"} height={isMobile ? "24" : "28"} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div>
              <div style={{
                fontSize: isMobile ? '0.875rem' : '0.9375rem',
                fontWeight: '600',
                color: '#92400e',
                marginBottom: '0.25rem'
              }}>
                Unable to load the purchases
              </div>
              <div style={{
                fontSize: isMobile ? '0.75rem' : '0.8125rem',
                color: '#78350f',
                lineHeight: '1.4'
              }}>
                Please wait for some moment
              </div>
            </div>
          </div>
        </div>
      ) : recentPurchases.length === 0 ? (
        <div style={{
          background: 'rgba(156, 163, 175, 0.1)',
          border: '1px solid rgba(156, 163, 175, 0.2)',
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          color: '#6b7280'
        }}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" style={{ margin: '0 auto 1rem auto', opacity: 0.5 }}>
            <path d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6"/>
          </svg>
          <div style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            No Recent Purchases
          </div>
          <div style={{ fontSize: '0.85rem' }}>
            No purchase orders found
          </div>
        </div>
      ) : (
        <div 
          className="recent-purchases-scroll"
          style={{
            display: 'grid',
            gap: '1rem',
            maxHeight: isMobile ? '300px' : '400px',
            overflowY: 'auto',
            overflowX: 'hidden',
            scrollbarWidth: 'thin',
            scrollbarColor: '#10b981 #f1f5f9',
            paddingRight: '0.5rem'
          }}>
          {recentPurchases.map((purchase, index) => (
            <div key={purchase.id || index} style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid rgba(16, 185, 129, 0.1)',
              padding: '1rem',
              boxShadow: '0 2px 8px rgba(16, 185, 129, 0.05)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.05)';
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.75rem'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '0.9rem',
                    fontWeight: '700',
                    color: '#1f2937',
                    marginBottom: '0.25rem'
                  }}>
                    Purchase #{purchase.id || purchase.purchase_id || index + 1}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#6b7280',
                    marginBottom: '0.25rem'
                  }}>
                    {purchase.created_at ? new Date(purchase.created_at).toLocaleString() : 'Date not available'}
                  </div>
                  {purchase.distributor_name && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#9ca3af'
                    }}>
                      Distributor: {purchase.distributor_name}
                    </div>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  {purchase.total_amount && (
                    <div style={{
                      background: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '6px',
                      fontSize: '0.8rem',
                      fontWeight: '600'
                    }}>
                      ₹{purchase.total_amount}
                    </div>
                  )}
                  {purchase.status === 'received' || purchase.status === 'completed' ? (
                    <div style={{
                      background: 'rgba(34, 197, 94, 0.1)',
                      color: '#16a34a',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem'
                    }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4"/>
                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                      </svg>
                      Received
                    </div>
                  ) : (
                    <button
                      onClick={() => handleReceivePurchase(purchase.id || purchase.purchase_id)}
                      disabled={receivingPurchaseId === (purchase.id || purchase.purchase_id)}
                      style={{
                        background: receivingPurchaseId === (purchase.id || purchase.purchase_id)
                          ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                          : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 0.75rem',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        cursor: receivingPurchaseId === (purchase.id || purchase.purchase_id) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.375rem',
                        opacity: receivingPurchaseId === (purchase.id || purchase.purchase_id) ? 0.7 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (receivingPurchaseId !== (purchase.id || purchase.purchase_id)) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (receivingPurchaseId !== (purchase.id || purchase.purchase_id)) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                    >
                      {receivingPurchaseId === (purchase.id || purchase.purchase_id) ? (
                        <div style={{
                          width: '12px',
                          height: '12px',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderTop: '2px solid white',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></div>
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9 12l2 2 4-4"/>
                          <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"/>
                        </svg>
                      )}
                      {receivingPurchaseId === (purchase.id || purchase.purchase_id) ? 'Receiving...' : 'Receive'}
                    </button>
                  )}
                </div>
              </div>
              {purchase.items && purchase.items.length > 0 && (
                <div style={{
                  fontSize: '0.75rem',
                  color: '#6b7280',
                  borderTop: '1px solid rgba(16, 185, 129, 0.1)',
                  paddingTop: '0.5rem'
                }}>
                  Items: {purchase.items.length} • Status: {purchase.status || 'Pending'}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <style>{`
        @keyframes slideDownFromTop {
          0% {
            transform: translateY(-100%);
            opacity: 0;
          }
          100% {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Custom scrollbar for recent purchases */
        .recent-purchases-scroll::-webkit-scrollbar {
          width: 6px;
        }

        .recent-purchases-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }

        .recent-purchases-scroll::-webkit-scrollbar-thumb {
          background: #10b981;
          border-radius: 3px;
        }

        .recent-purchases-scroll::-webkit-scrollbar-thumb:hover {
          background: #059669;
        }
      `}</style>
    </>
  );
};

export default RecentPurchasesBlock;

